import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import {
  FiSave, FiUser, FiMapPin, FiBriefcase, FiHash,
  FiEdit3, FiX, FiMail, FiPhone, FiGlobe, FiShield,
  FiCheckCircle, FiAlertCircle, FiZap, FiLayers
} from "react-icons/fi";
import axios from "axios";
import { baseurl } from "../../baseurl";
import Swal from "sweetalert2";
import { State, City } from "country-state-city";

/* ═══════════════════════════════════ STYLES ═══════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap');

:root {
  --t:    #0a5556;
  --t2:   #0c7070;
  --t3:   #14b8a6;
  --t4:   #5eead4;
  --gold: #FFD166;
  --gold2:#d48a00;
  --bg:   #edf7f7;
  --surface: rgba(255,255,255,0.94);
  --border:  rgba(10,85,86,0.11);
  --danger:  #ef4444;
  --success: #10b981;
  --warn:    #f59e0b;
  --text:    #07282a;
  --muted:   #5a8080;
  --r:    18px;
  --r-sm: 10px;
  --sh:   0 2px 16px rgba(10,85,86,0.08), 0 1px 3px rgba(10,85,86,0.05);
  --sh-md:0 8px 32px rgba(10,85,86,0.11), 0 2px 8px rgba(10,85,86,0.06);
}

@keyframes bp-up    { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
@keyframes bp-in    { from{opacity:0} to{opacity:1} }
@keyframes bp-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
@keyframes bp-float2{ 0%,100%{transform:translateY(0)} 50%{transform:translateY(10px)} }
@keyframes bp-pulse { 0%,100%{box-shadow:0 0 0 0 rgba(10,85,86,0.3)} 60%{box-shadow:0 0 0 8px rgba(10,85,86,0)} }
@keyframes bp-spin  { to{transform:rotate(360deg)} }
@keyframes bp-shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }

/* ── PAGE ── */
.bp-page { font-family:'DM Sans',sans-serif;background:var(--bg);min-height:100vh;position:relative;overflow-x:hidden; }
.bp-blob { position:fixed;border-radius:50%;pointer-events:none;z-index:0; }
.bp-blob-1 { width:460px;height:460px;top:-140px;right:-100px;background:radial-gradient(circle at 35% 35%,rgba(10,85,86,0.10),transparent 65%);animation:bp-float 12s ease-in-out infinite; }
.bp-blob-2 { width:360px;height:360px;bottom:40px;left:-80px;background:radial-gradient(circle at 65% 65%,rgba(20,184,166,0.09),transparent 65%);animation:bp-float2 15s ease-in-out infinite; }
.bp-blob-3 { width:200px;height:200px;top:45%;right:6%;background:radial-gradient(circle,rgba(255,209,102,0.07),transparent 68%);animation:bp-float 10s ease-in-out infinite 2.5s; }
.bp-grid  { position:fixed;inset:0;pointer-events:none;z-index:0;background-image:linear-gradient(rgba(10,85,86,0.022) 1px,transparent 1px),linear-gradient(90deg,rgba(10,85,86,0.022) 1px,transparent 1px);background-size:56px 56px; }

.bp-wrap { position:relative;z-index:1;padding:24px 24px 68px;animation:bp-in 0.4s ease; }

/* ── PAGE HEADER ── */
.bp-phd { display:flex;align-items:center;justify-content:space-between;margin-bottom:26px;animation:bp-up 0.5s ease; }
.bp-phd-left { display:flex;align-items:center;gap:15px; }
.bp-phd-icon { width:50px;height:50px;border-radius:16px;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:21px;color:#fff;background:linear-gradient(135deg,var(--t),var(--t3));box-shadow:0 8px 24px rgba(10,85,86,0.30); }
.bp-phd-title { font-family:'Sora',sans-serif;font-size:23px;font-weight:800;color:var(--text);letter-spacing:-.04em;margin:0 0 3px;line-height:1; }
.bp-phd-sub   { font-size:12.5px;color:var(--muted);margin:0; }
.bp-phd-right { display:flex;align-items:center;gap:10px; }

/* ── BUTTONS ── */
.bp-btn-edit { display:inline-flex;align-items:center;gap:7px;padding:10px 20px;border-radius:11px;border:none;background:linear-gradient(135deg,var(--t),var(--t3));color:#fff;font-family:'Sora',sans-serif;font-size:12.5px;font-weight:700;cursor:pointer;box-shadow:0 6px 20px rgba(10,85,86,0.28);transition:all .25s; }
.bp-btn-edit:hover { transform:translateY(-2px);box-shadow:0 12px 28px rgba(10,85,86,0.36); }
.bp-btn-cancel { display:inline-flex;align-items:center;gap:7px;padding:10px 20px;border-radius:11px;border:1.5px solid var(--border);background:var(--surface);color:var(--t);font-family:'Sora',sans-serif;font-size:12.5px;font-weight:700;cursor:pointer;transition:all .2s;backdrop-filter:blur(12px); }
.bp-btn-cancel:hover { background:rgba(10,85,86,0.05); }
.bp-btn-cancel:disabled,.bp-btn-edit:disabled { opacity:.55;cursor:not-allowed;transform:none; }
.bp-btn-save { display:inline-flex;align-items:center;gap:7px;padding:10px 20px;border-radius:11px;border:none;background:linear-gradient(135deg,var(--t),var(--t3));color:#fff;font-family:'Sora',sans-serif;font-size:12.5px;font-weight:700;cursor:pointer;box-shadow:0 6px 20px rgba(10,85,86,0.28);transition:all .25s; }
.bp-btn-save:hover { transform:translateY(-2px);box-shadow:0 12px 28px rgba(10,85,86,0.36); }
.bp-btn-save:disabled { opacity:.6;cursor:not-allowed;transform:none; }

/* ── SECTION LABEL ── */
.bp-sec { display:flex;align-items:center;gap:8px;margin-bottom:13px; }
.bp-sec-dot { width:30px;height:30px;border-radius:9px;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:13px;color:#fff;background:linear-gradient(135deg,var(--t),var(--t3));box-shadow:0 3px 10px rgba(10,85,86,0.24); }
.bp-sec-lbl { font-family:'Sora',sans-serif;font-size:13.5px;font-weight:700;color:var(--t);letter-spacing:.01em; }

/* ── CARD ── */
.bp-card { background:var(--surface);backdrop-filter:blur(24px);border:1px solid rgba(255,255,255,0.88);border-radius:var(--r);box-shadow:var(--sh);overflow:hidden;position:relative;margin-bottom:18px; }
.bp-card::before { content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,var(--t) 0%,var(--t3) 55%,var(--gold) 100%); }
.bp-card-body { padding:22px 24px; }

/* ── LAYOUT ── */
.bp-layout { display:grid;grid-template-columns:1fr 320px;gap:20px;align-items:start; }

/* ── FORM ELEMENTS ── */
.bp-label { display:block;font-size:11px;font-weight:700;color:var(--t);letter-spacing:.065em;text-transform:uppercase;margin-bottom:6px;font-family:'Sora',sans-serif; }
.bp-input {
  width:100%;padding:10px 13px;
  background:rgba(10,85,86,0.035);
  border:1.5px solid rgba(10,85,86,0.13);
  border-radius:var(--r-sm);
  font-size:13px;color:var(--text);font-family:'DM Sans',sans-serif;
  outline:none;transition:border-color .2s,box-shadow .2s,background .2s;
  box-sizing:border-box;
}
.bp-input:focus { border-color:var(--t);background:#fff;box-shadow:0 0 0 3px rgba(10,85,86,0.10); }
.bp-input::placeholder { color:#9abcbc; }
.bp-input:disabled { opacity:.6;cursor:not-allowed;background:rgba(10,85,86,0.03); }
.bp-input[type="email"]:disabled { background:rgba(10,85,86,0.03); }
.bp-select {
  width:100%;padding:10px 13px;
  background:rgba(10,85,86,0.035);
  border:1.5px solid rgba(10,85,86,0.13);
  border-radius:var(--r-sm);
  font-size:13px;color:var(--text);font-family:'DM Sans',sans-serif;
  outline:none;cursor:pointer;transition:border-color .2s,box-shadow .2s;
  box-sizing:border-box;
  appearance:none;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%235a8080' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat:no-repeat;
  background-position:right 13px center;
  padding-right:36px;
}
.bp-select:focus { border-color:var(--t);box-shadow:0 0 0 3px rgba(10,85,86,0.10);background-color:#fff; }
.bp-select:disabled { opacity:.6;cursor:not-allowed;background-color:rgba(10,85,86,0.03); }

.bp-field { margin-bottom:16px; }
.bp-field:last-child { margin-bottom:0; }

/* ── GRID HELPERS ── */
.bp-g2 { display:grid;grid-template-columns:1fr 1fr;gap:14px; }

/* ── DIVIDER ── */
.bp-divider { height:1px;background:var(--border);margin:18px 0; }

/* ── READ-ONLY LOCKED ── */
.bp-locked { background:rgba(10,85,86,0.03) !important;cursor:not-allowed; }
.bp-lock-badge { display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:50px;background:rgba(10,85,86,0.07);border:1px solid var(--border);font-size:10px;font-weight:700;color:var(--muted);font-family:'Sora',sans-serif;margin-left:8px;text-transform:none;letter-spacing:0; }

/* ── AVATAR BLOCK ── */
.bp-avatar-block { display:flex;flex-direction:column;align-items:center;padding:28px 24px 20px; }
.bp-avatar { width:80px;height:80px;border-radius:50%;background:linear-gradient(135deg,var(--t),var(--t3));display:flex;align-items:center;justify-content:center;font-family:'Sora',sans-serif;font-size:28px;font-weight:800;color:#fff;margin-bottom:14px;box-shadow:0 8px 26px rgba(10,85,86,0.28);position:relative;flex-shrink:0; }
.bp-avatar-ring { position:absolute;inset:-4px;border-radius:50%;border:2.5px solid transparent;background:linear-gradient(#fff,#fff) padding-box,linear-gradient(135deg,var(--t3),var(--gold)) border-box; }
.bp-biz-name { font-family:'Sora',sans-serif;font-size:15px;font-weight:800;color:var(--text);margin:0 0 3px;text-align:center; }
.bp-biz-email { font-size:12px;color:var(--muted);margin:0 0 12px;text-align:center; }
.bp-biz-tag  { display:inline-flex;align-items:center;gap:5px;padding:4px 12px;border-radius:50px;background:rgba(10,85,86,0.08);border:1px solid var(--border);font-size:11px;font-weight:700;color:var(--t);font-family:'Sora',sans-serif; }
.bp-biz-divider { width:100%;height:1px;background:var(--border);margin:16px 0; }
.bp-biz-meta { width:100%; }
.bp-biz-meta-row { display:flex;align-items:center;gap:9px;padding:8px 0;border-bottom:1px solid rgba(10,85,86,0.06);font-size:12.5px; }
.bp-biz-meta-row:last-child { border-bottom:none; }
.bp-biz-meta-icon { width:26px;height:26px;border-radius:7px;background:rgba(10,85,86,0.07);display:flex;align-items:center;justify-content:center;color:var(--t);font-size:12px;flex-shrink:0; }
.bp-biz-meta-label { font-size:10.5px;color:var(--muted);margin-bottom:1px; }
.bp-biz-meta-val   { font-weight:600;color:var(--text);font-size:12px; }

/* ── CLASSIFICATION CARD ── */
.bp-class-options { display:flex;flex-direction:column;gap:8px; }
.bp-class-opt { display:flex;align-items:center;gap:10px;padding:11px 14px;border-radius:10px;border:1.5px solid var(--border);background:rgba(10,85,86,0.03);cursor:pointer;transition:all .18s; }
.bp-class-opt.selected { border-color:var(--t);background:rgba(10,85,86,0.07);box-shadow:0 0 0 3px rgba(10,85,86,0.06); }
.bp-class-opt:hover:not(.disabled) { border-color:rgba(10,85,86,0.28);background:rgba(10,85,86,0.05); }
.bp-class-opt.disabled { opacity:.55;cursor:not-allowed; }
.bp-class-radio { width:16px;height:16px;border-radius:50%;border:2px solid var(--border);display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all .18s; }
.bp-class-radio.on { border-color:var(--t);background:var(--t); }
.bp-class-radio.on::after { content:'';width:6px;height:6px;border-radius:50%;background:#fff; }
.bp-class-label { font-family:'Sora',sans-serif;font-size:12.5px;font-weight:700;color:var(--text); }
.bp-class-sub   { font-size:10.5px;color:var(--muted);margin-top:1px; }

/* ── TIP CARD ── */
.bp-tip { background:linear-gradient(135deg,rgba(10,85,86,0.06),rgba(20,184,166,0.04));border:1.5px solid rgba(10,85,86,0.12);border-radius:14px;padding:16px 18px;display:flex;gap:12px; }
.bp-tip-icon { width:34px;height:34px;border-radius:10px;background:linear-gradient(135deg,var(--t),var(--t3));display:flex;align-items:center;justify-content:center;color:#fff;font-size:14px;flex-shrink:0;box-shadow:0 4px 12px rgba(10,85,86,0.24); }
.bp-tip-title { font-family:'Sora',sans-serif;font-size:12.5px;font-weight:700;color:var(--t);margin:0 0 4px; }
.bp-tip-text  { font-size:12px;color:var(--muted);margin:0;line-height:1.6; }

/* ── EDIT MODE INDICATOR ── */
.bp-edit-banner { display:flex;align-items:center;gap:9px;padding:11px 16px;border-radius:11px;background:rgba(255,209,102,0.12);border:1.5px solid rgba(255,209,102,0.35);margin-bottom:20px;animation:bp-up 0.3s ease; }
.bp-edit-banner-icon { width:28px;height:28px;border-radius:8px;background:rgba(255,209,102,0.25);display:flex;align-items:center;justify-content:center;color:var(--gold2);font-size:13px;flex-shrink:0; }
.bp-edit-banner-text { font-size:12.5px;color:#7a4f00;font-weight:500; }
.bp-edit-banner-text strong { font-family:'Sora',sans-serif;font-weight:700; }

/* ── LOADING OVERLAY ── */
.bp-loading-wrap { display:flex;align-items:center;justify-content:center;min-height:100vh;background:var(--bg); }
.bp-spinner { width:44px;height:44px;border-radius:50%;border:3.5px solid rgba(10,85,86,0.12);border-top-color:var(--t);animation:bp-spin 0.75s linear infinite; }

/* ── RESPONSIVE ── */
@media(max-width:1024px) { .bp-layout{grid-template-columns:1fr;} }
@media(max-width:600px)  { .bp-g2{grid-template-columns:1fr;} .bp-wrap{padding:14px 14px 52px;} .bp-phd{flex-direction:column;align-items:flex-start;gap:14px;} }
`;

/* ═══════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════ */
export default function BusinessProfile() {
  const [profile, setProfile] = useState({
    email: "",
    business_name: "",
    contact_email: "",
    whatsapp_number: "",
    employee_size: "",
    business_type: "",
    country: "India",
    state: "",
    city: "",
    gst: "",
    zipcode: ""
  });

  const [isEditing, setIsEditing] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [fetching,  setFetching]  = useState(true);

  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);

  const token = localStorage.getItem("auth_token");

  const Toast = Swal.mixin({
    toast: true, position: "top-end",
    showConfirmButton: false, timer: 3000, timerProgressBar: true,
  });

  /* ── state init ── */
  useEffect(() => { setStatesList(State.getStatesOfCountry("IN")); }, []);

  useEffect(() => {
    const selectedState = statesList.find(s => s.name === profile.state);
    if (selectedState) setCitiesList(City.getCitiesOfState("IN", selectedState.isoCode));
    else setCitiesList([]);
  }, [profile.state, statesList]);

  /* ── fetch ── */
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await axios.get(`${baseurl}getBusinessInfo`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          setProfile({ ...response.data.data, country: "India" });
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally { setFetching(false); }
    };
    fetchBusinessInfo();
  }, [token]);

  const updateField = (key, value) =>
    setProfile(prev => ({ ...prev, [key]: value }));

  /* ── submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${baseurl}saveBusinessInfo`, profile, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (response.data.success) {
        Toast.fire({ icon: "success", title: "Profile updated successfully" });
        setIsEditing(false);
      } else {
        Toast.fire({ icon: "error", title: response.data.message || "Failed to update" });
      }
    } catch (error) {
      Toast.fire({ icon: "error", title: "Server error occurred" });
    } finally { setLoading(false); }
  };

  /* ── initials ── */
  const getInitials = (name) => {
    if (!name) return "B";
    return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  };

  /* ── loading state ── */
  if (fetching) {
    return (
      <>
        <style>{CSS}</style>
        <Topbar /><Sidebar />
        <main className="main-wrapper">
          <div className="bp-loading-wrap">
            <div className="bp-spinner"/>
          </div>
        </main>
      </>
    );
  }

  /* ── biz type options ── */
  const bizTypeOptions = [
    { val:"Product", label:"Product Based", sub:"Goods / Manufacturing" },
    { val:"Service", label:"Service Based", sub:"Consulting / SaaS / Agencies" },
    { val:"Both",    label:"Both",          sub:"Products & Services" },
  ];
  const empSizeOptions = [
    { val:"1 - 10",  label:"1 – 10",   sub:"Micro / Startup" },
    { val:"11 - 50", label:"11 – 50",  sub:"Small Business" },
    { val:"50+",     label:"50+",      sub:"Mid-size / Enterprise" },
  ];

  /* ═══════════ RENDER ═══════════ */
  return (
    <>
      <style>{CSS}</style>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="bp-page">
          <div className="bp-blob bp-blob-1"/>
          <div className="bp-blob bp-blob-2"/>
          <div className="bp-blob bp-blob-3"/>
          <div className="bp-grid"/>

          <div className="bp-wrap">

            {/* ── Page Header ── */}
            <div className="bp-phd">
              <div className="bp-phd-left">
                <div className="bp-phd-icon"><FiBriefcase/></div>
                <div>
                  <h2 className="bp-phd-title">Business Profile</h2>
                  <p className="bp-phd-sub">Manage your business identity, location &amp; settings</p>
                </div>
              </div>
              <div className="bp-phd-right">
                {!isEditing ? (
                  <button type="button" className="bp-btn-edit" onClick={() => setIsEditing(true)}>
                    <FiEdit3 size={13}/> Edit Profile
                  </button>
                ) : (
                  <>
                    <button type="button" className="bp-btn-cancel" onClick={() => setIsEditing(false)} disabled={loading}>
                      <FiX size={13}/> Cancel
                    </button>
                    <button form="profile-form" type="submit" className="bp-btn-save" disabled={loading}>
                      {loading
                        ? <><div style={{width:13,height:13,border:"2px solid rgba(255,255,255,0.4)",borderTopColor:"#fff",borderRadius:"50%",animation:"bp-spin 0.7s linear infinite"}}/> Saving…</>
                        : <><FiSave size={13}/> Save Changes</>
                      }
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* ── Editing Banner ── */}
            {isEditing && (
              <div className="bp-edit-banner">
                <div className="bp-edit-banner-icon"><FiEdit3 size={13}/></div>
                <div className="bp-edit-banner-text">
                  <strong>Edit mode active</strong> — Make your changes and click Save Changes to update your profile.
                </div>
              </div>
            )}

            <form id="profile-form" onSubmit={handleSubmit}>
              <div className="bp-layout">

                {/* ──────── LEFT COLUMN ──────── */}
                <div>

                  {/* General Information */}
                  <div className="bp-sec">
                    <div className="bp-sec-dot"><FiBriefcase size={13}/></div>
                    <span className="bp-sec-lbl">General Information</span>
                  </div>
                  <div className="bp-card">
                    <div className="bp-card-body">
                      <div className="bp-field">
                        <label className="bp-label">Business Name</label>
                        <input type="text" className="bp-input" value={profile.business_name || ""}
                          onChange={e => updateField("business_name", e.target.value)}
                          disabled={!isEditing} required placeholder="Your business name"/>
                      </div>
                      <div className="bp-g2">
                        <div className="bp-field">
                          <label className="bp-label">
                            Primary Email
                            <span className="bp-lock-badge"><FiShield size={9}/> Read only</span>
                          </label>
                          <input type="email" className="bp-input bp-locked" value={profile.email || ""} disabled/>
                        </div>
                        <div className="bp-field">
                          <label className="bp-label">Contact Email</label>
                          <input type="email" className="bp-input" value={profile.contact_email || ""}
                            onChange={e => updateField("contact_email", e.target.value)}
                            disabled={!isEditing} placeholder="contact@yourbiz.com"/>
                        </div>
                      </div>
                      <div className="bp-g2">
                        <div className="bp-field">
                          <label className="bp-label">WhatsApp Number</label>
                          <input type="text" className="bp-input" value={profile.whatsapp_number || ""}
                            onChange={e => updateField("whatsapp_number", e.target.value)}
                            disabled={!isEditing} placeholder="+91 98765 43210"/>
                        </div>
                        <div className="bp-field">
                          <label className="bp-label">GST Number</label>
                          <input type="text" className="bp-input" style={{ textTransform:"uppercase" }}
                            value={profile.gst || ""}
                            onChange={e => updateField("gst", e.target.value)}
                            disabled={!isEditing} placeholder="22AAAAA0000A1Z5"/>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location Details */}
                  <div className="bp-sec">
                    <div className="bp-sec-dot"><FiMapPin size={13}/></div>
                    <span className="bp-sec-lbl">Location Details</span>
                  </div>
                  <div className="bp-card">
                    <div className="bp-card-body">
                      <div className="bp-g2">
                        <div className="bp-field">
                          <label className="bp-label">
                            Country
                            <span className="bp-lock-badge"><FiGlobe size={9}/> India only</span>
                          </label>
                          <input type="text" className="bp-input bp-locked" value="India" disabled/>
                        </div>
                        <div className="bp-field">
                          <label className="bp-label">State</label>
                          <select className="bp-select" value={profile.state || ""}
                            onChange={e => { updateField("state", e.target.value); updateField("city", ""); }}
                            disabled={!isEditing} required>
                            <option value="">Select State</option>
                            {statesList.map(state => (
                              <option key={state.isoCode} value={state.name}>{state.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="bp-g2">
                        <div className="bp-field">
                          <label className="bp-label">City</label>
                          <select className="bp-select" value={profile.city || ""}
                            onChange={e => updateField("city", e.target.value)}
                            disabled={!isEditing || !profile.state} required>
                            <option value="">Select City</option>
                            {citiesList.map((city, index) => (
                              <option key={index} value={city.name}>{city.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="bp-field">
                          <label className="bp-label">ZIP Code</label>
                          <input type="text" className="bp-input" value={profile.zipcode || ""}
                            onChange={e => updateField("zipcode", e.target.value)}
                            disabled={!isEditing} placeholder="400001"/>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>

                {/* ──────── RIGHT SIDEBAR ──────── */}
                <div>

                  {/* Profile Card */}
                  <div className="bp-card" style={{ marginBottom: 18 }}>
                    <div className="bp-avatar-block">
                      <div className="bp-avatar">
                        <div className="bp-avatar-ring"/>
                        {getInitials(profile.business_name)}
                      </div>
                      <div className="bp-biz-name">{profile.business_name || "Your Business"}</div>
                      <div className="bp-biz-email">{profile.email || "—"}</div>
                      {profile.business_type && (
                        <span className="bp-biz-tag">
                          <FiLayers size={10}/> {profile.business_type} Based
                        </span>
                      )}
                      <div className="bp-biz-divider"/>
                      <div className="bp-biz-meta">
                        {[
                          { icon:<FiMapPin size={11}/>,  label:"Location", val: profile.city && profile.state ? `${profile.city}, ${profile.state}` : "Not set" },
                          { icon:<FiPhone size={11}/>,   label:"WhatsApp", val: profile.whatsapp_number || "Not set" },
                          { icon:<FiMail size={11}/>,    label:"Contact",  val: profile.contact_email || "Not set" },
                          { icon:<FiShield size={11}/>,  label:"GST",      val: profile.gst || "Not provided" },
                        ].map((m, i) => (
                          <div key={i} className="bp-biz-meta-row">
                            <div className="bp-biz-meta-icon">{m.icon}</div>
                            <div>
                              <div className="bp-biz-meta-label">{m.label}</div>
                              <div className="bp-biz-meta-val">{m.val}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Classification */}
                  <div className="bp-sec">
                    <div className="bp-sec-dot"><FiHash size={13}/></div>
                    <span className="bp-sec-lbl">Classification</span>
                  </div>
                  <div className="bp-card">
                    <div className="bp-card-body">
                      <div className="bp-field">
                        <label className="bp-label">Business Type</label>
                        <div className="bp-class-options" style={{ marginTop: 8 }}>
                          {bizTypeOptions.map(opt => (
                            <div key={opt.val}
                              className={`bp-class-opt ${profile.business_type === opt.val ? "selected" : ""} ${!isEditing ? "disabled" : ""}`}
                              onClick={() => isEditing && updateField("business_type", opt.val)}>
                              <div className={`bp-class-radio ${profile.business_type === opt.val ? "on" : ""}`}/>
                              <div>
                                <div className="bp-class-label">{opt.label}</div>
                                <div className="bp-class-sub">{opt.sub}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bp-divider"/>

                      <div className="bp-field" style={{ marginBottom: 0 }}>
                        <label className="bp-label">Team Size</label>
                        <div className="bp-class-options" style={{ marginTop: 8 }}>
                          {empSizeOptions.map(opt => (
                            <div key={opt.val}
                              className={`bp-class-opt ${profile.employee_size === opt.val ? "selected" : ""} ${!isEditing ? "disabled" : ""}`}
                              onClick={() => isEditing && updateField("employee_size", opt.val)}>
                              <div className={`bp-class-radio ${profile.employee_size === opt.val ? "on" : ""}`}/>
                              <div>
                                <div className="bp-class-label">{opt.label}</div>
                                <div className="bp-class-sub">{opt.sub}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tip */}
                  <div className="bp-tip">
                    <div className="bp-tip-icon"><FiZap size={14}/></div>
                    <div>
                      <div className="bp-tip-title">India Region</div>
                      <div className="bp-tip-text">Currently serving <strong>India only</strong>. Select your state first to load the city dropdown.</div>
                    </div>
                  </div>

                </div>
              </div>
            </form>

          </div>
        </div>
      </main>
    </>
  );
}
